// Etiquetas, colores y motivos de la pantalla Clientes del Portal Admin SureKeep. Es la mirada de
// CRÉDITO: SCI, nivel, saldo insoluto y mora — no las compras del cliente.
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';
import { LoanPaymentOriginEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanPaymentOriginEnum';
import { LoanCreditStatusEnum } from '@fiado/type-kit/bin/loanCredit/enums/LoanCreditStatusEnum';
import type { CreditCustomerCredit } from '@/api/services/loan-credit';
import type { CreditSciSource } from '@/domains/sales/portal/customers/lib/creditPortfolio';
import { formatShortDateMx } from '@/lib/dateHelpers';
import type { CustomerListState } from './creditCustomersErrors';
import type { Gap } from './customer360Gaps';

/**
 * El guion del mock para un dato LEÍDO que vino vacío. En la tabla el motivo baja al `title`: 163
 * filas repitiéndolo en pantalla son un muro. Nunca se presta a una lectura fallida o sin permiso.
 */
export const EMPTY_CELL = '—';

/** Hasta acá el id entra entero en la columna; más largo, se muestra la cola. */
const WHOLE_ID_MAX_LENGTH = 10;

/** Cuántos caracteres del final alcanzan para distinguir una fila de otra. */
const ID_TAIL_LENGTH = 6;

/**
 * El id recortado para la columna del mock, que mide ~58px. El mock dibujó ids tipo `CL-0004`, pero
 * el silo devuelve ULIDs de 26 caracteres: no entran, y su prefijo es la marca de tiempo, igual
 * entre dos clientes del mismo día. La cola es la parte que los distingue. El id ENTERO vive en el
 * `title` de la celda y en la ficha.
 */
export function shortCustomerId(retailCustomerId: string): string {
    if (retailCustomerId.length <= WHOLE_ID_MAX_LENGTH) return retailCustomerId;
    return `…${retailCustomerId.slice(-ID_TAIL_LENGTH)}`;
}

const MXN = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });

/** Importe completo en pesos. Los `*Cents` del silo son enteros y se dividen antes de formatear. */
export function formatMoneyCents(cents: number): string {
    return MXN.format(cents / 100);
}

const THOUSAND = 1000;
const MILLION = 1_000_000;

/** Importe corto de la celda angosta: `$1.2K` / `$1.5M`, igual que el mock. */
export function formatMoneyShortCents(cents: number): string {
    const pesos = cents / 100;
    if (Math.abs(pesos) >= MILLION) return `$${(pesos / MILLION).toFixed(1)}M`;
    if (Math.abs(pesos) >= THOUSAND) return `$${(pesos / THOUSAND).toFixed(1)}K`;
    return `$${String(Math.round(pesos))}`;
}

/** Solo los dígitos: la UI muestra `55 1234 0001` y quien copia lo que ve teclea los espacios. */
export function digitsOf(value: string): string {
    return value.replace(/\D/g, '');
}

/** Teléfono a 10 dígitos con separación mexicana: `55 1234 0001`. */
export function formatPhone(phone: string): string {
    const digits = digitsOf(phone);
    if (digits.length !== 10) return phone;
    return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
}

/** Un cliente que la venta nunca resolvió: se dice, no se deja la celda en blanco. */
export const UNNAMED_CUSTOMER = 'Sin nombre';

/** Por qué la fila no trae nombre. En la tabla es el `title` del guion; en la ficha, la etiqueta. */
export const UNNAMED_CUSTOMER_NOTE =
    'La venta que dio de alta a este cliente nunca resolvió su nombre.';

/* ================================================================================
   NIVEL — el tramo del SCI. Lo pinta `TierPill`; aquí solo vive su orden.
   ================================================================================ */

/** Orden del peor al mejor, para ordenar la columna Nivel de menos a más. */
export const LEVEL_ORDER: Record<ClientLevelEnum, number> = {
    [ClientLevelEnum.NOT_ELIGIBLE]: 0,
    [ClientLevelEnum.BRONZE]: 1,
    [ClientLevelEnum.SILVER]: 2,
    [ClientLevelEnum.GOLD]: 3,
};

/* ================================================================================
   SCI — el score 0-100. Celda aparte del nivel.
   ================================================================================ */

const SCI_AT_ORIGINATION_NOTE =
    'SCI CONGELADO al originar su crédito más reciente. No es el SCI vivo del cliente de hoy.';

const SCI_LIVE_NOTE = 'SCI vivo del cliente, leído de su acreditado.';

/** De dónde salió el SCI que se pinta: el vivo del acreditado o el congelado al originar. */
export function sciSourceNote(source: CreditSciSource | null): string {
    return source === 'BORROWER' ? SCI_LIVE_NOTE : SCI_AT_ORIGINATION_NOTE;
}

const SCI_NO_CREDIT_NOTE =
    'Este cliente no tiene créditos: no hay originación de la que salga un SCI.';

const SCI_NOT_RECORDED_NOTE =
    'Su crédito más reciente no dejó registrado el SCI con el que se originó.';

const LEVEL_NO_CREDIT_NOTE =
    'Este cliente no tiene créditos: el motor todavía no le asignó un nivel.';

const LEVEL_NOT_RECORDED_NOTE =
    'Su crédito más reciente no dejó registrado el nivel con el que se originó.';

/** Por qué no hay SCI: sin créditos no hubo originación, y con créditos el silo no lo guardó. */
export function sciMissingNote(creditsCount: number): string {
    return creditsCount === 0 ? SCI_NO_CREDIT_NOTE : SCI_NOT_RECORDED_NOTE;
}

/** Por qué no hay nivel. Los mismos dos motivos que el SCI del que se deriva. */
export function levelMissingNote(creditsCount: number): string {
    return creditsCount === 0 ? LEVEL_NO_CREDIT_NOTE : LEVEL_NOT_RECORDED_NOTE;
}

/* ================================================================================
   ENCABEZADOS — qué mide cada columna. Vivía en una leyenda fija bajo la tabla que el mock no
   tiene; ahora cada encabezado dice lo suyo.
   ================================================================================ */

export const LEVEL_COLUMN_NOTE =
    'El tramo en el que cae el SCI del cliente. El SCI es el score y el Nivel su tramo: son dos cosas.';

export const SCI_COLUMN_NOTE =
    'El score de crédito del cliente, de 0 a 100. El Nivel de la columna anterior es su tramo.';

export const ACTIVE_CREDITS_COLUMN_NOTE =
    'Cuántos créditos VIGENTES tiene el cliente. Su historial completo se ve en la ficha.';

export const BALANCE_COLUMN_NOTE =
    'El saldo insoluto de sus créditos vigentes: solo el capital que falta, sin interés ni IVA.';

export const TOTAL_DEBT_COLUMN_NOTE =
    'La deuda total de sus créditos vigentes: capital, interés e IVA que faltan. Abajo, el saldo insoluto (solo capital).';

/** Por qué la celda de deuda total va vacía con un crédito vigente: el silo no la mandó. */
export const TOTAL_DEBT_MISSING_NOTE =
    'Este cliente no trae su deuda total: no se sabe cuánto debe, y no es cero.';

/** Qué significa la columna. Un cliente cae en 0-N segmentos; sin ninguno, en el por omisión. */
export const SEGMENT_COLUMN_NOTE =
    'Los segmentos de crédito del tenant a los que pertenece el cliente. Puede estar en varios a la vez, y quien no está en ninguno queda en el segmento por omisión. No es el segmento de compras de retail, que mide otra cosa.';

/* ================================================================================
   ESTADO DEL CLIENTE — colapsa su cartera en una etiqueta operativa.
   ================================================================================ */

export type CustomerCreditStatus =
    | 'IN_ARREARS'
    | 'CURRENT'
    | 'WRITTEN_OFF'
    | 'SETTLED'
    | 'CANCELLED'
    | 'NO_CREDITS';

export const CUSTOMER_STATUS_LABEL: Record<CustomerCreditStatus, string> = {
    IN_ARREARS: 'En mora',
    CURRENT: 'Al día',
    WRITTEN_OFF: 'Baja contable',
    SETTLED: 'Liquidado',
    CANCELLED: 'Sin vigentes',
    NO_CREDITS: 'Sin créditos',
};

export const CUSTOMER_STATUS_CHIP_CLASS: Record<CustomerCreditStatus, string> = {
    IN_ARREARS: 'border-destructive bg-destructive/10 text-destructive',
    CURRENT: 'border-success bg-success/10 text-success',
    WRITTEN_OFF: 'border-destructive bg-destructive/10 text-destructive',
    SETTLED: 'border-info bg-info/10 text-info',
    CANCELLED: 'border-muted-foreground bg-muted text-muted-foreground',
    NO_CREDITS: 'border-border bg-muted text-muted-foreground',
};

/**
 * El chip con los días de mora cuando se saben. Sin `arrearsDays` dice «En mora» a secas: no hay
 * días que afirmar.
 */
export function customerStatusLabel(
    status: CustomerCreditStatus,
    arrearsDays: number | null,
): string {
    if (status !== 'IN_ARREARS' || arrearsDays === null) return CUSTOMER_STATUS_LABEL[status];
    return `En mora ${String(arrearsDays)}d`;
}

/** Qué significa cada estado en la operación. Los seis piden cosas distintas del analista. */
const CUSTOMER_STATUS_NOTE: Record<CustomerCreditStatus, string> = {
    IN_ARREARS:
        'Tiene al menos un crédito en mora. La cartera no trae los días vencidos de este cliente.',
    CURRENT: 'Tiene crédito vigente y ninguna cuota vencida.',
    WRITTEN_OFF:
        'No tiene crédito vigente y al menos uno se dio de baja contable por 180 días sin pago. Sí originó créditos con SureKeep.',
    SETTLED: 'No tiene crédito vigente y al menos uno de los que tuvo quedó liquidado.',
    CANCELLED:
        'Originó créditos pero ninguno quedó vigente ni liquidado: se cancelaron o se descartaron antes de activarse.',
    NO_CREDITS: 'Este cliente nunca originó un crédito con SureKeep.',
};

/** La frase entera detrás del chip. En mora con días sabidos dice cuántos, en vez del genérico. */
export function customerStatusNote(
    status: CustomerCreditStatus,
    arrearsDays: number | null,
): string {
    if (status !== 'IN_ARREARS' || arrearsDays === null) return CUSTOMER_STATUS_NOTE[status];
    return `${String(arrearsDays)} días desde el vencimiento de su cuota vencida más vieja.`;
}

/**
 * Por qué no hay LISTA. Sin ella no hay ni una fila que buscar por nombre, y un resultado vacío se
 * leería como «ningún cliente cumple».
 */
export const LIST_STATE_NOTE: Record<Exclude<CustomerListState, 'ready'>, string> = {
    loading: 'Todavía se está leyendo el padrón de clientes: no hay filas sobre las que filtrar.',
    error: 'No se pudo leer el padrón de clientes. Vuelve a intentar: sin lista no hay nada que filtrar.',
    forbidden:
        'No tienes permiso para ver el padrón de clientes. Reintentar no lo cambia; contacta a quien administra los roles.',
};

/* ================================================================================
   HUECOS SIN FUENTE — cada uno con SU motivo. Reusar uno para dos huecos es un bug.
   ================================================================================ */

/** Qué mide la columna Últ. pago. El BFF la resuelve por cliente: ya no es un hueco sin fuente. */
export const LAST_PAYMENT_COLUMN_NOTE =
    'La fecha en que se aplicó el último pago del cliente y dónde entró: la tienda que lo recibió o la cartera.';

/** Dónde se dice que entró un pago del motor: la cartera del cliente, no una tienda. */
const WALLET_PLACE_LABEL = 'Cartera';

const STORE_PLACE_FALLBACK = 'Tienda';

/** «08/oct · Tienda Limpia», «08/oct · Cartera» o solo la fecha en créditos viejos; null sin pago. */
export function lastPaymentLabelOf(
    credit: Pick<CreditCustomerCredit, 'lastPaymentAt' | 'lastPayment'>,
): string | null {
    if (!credit.lastPaymentAt) return null;
    const day = formatShortDateMx(credit.lastPayment?.at ?? credit.lastPaymentAt);
    if (!credit.lastPayment) return day;
    const place =
        credit.lastPayment.origin === LoanPaymentOriginEnum.IN_STORE
            ? (credit.lastPayment.storeName ?? STORE_PLACE_FALLBACK)
            : WALLET_PLACE_LABEL;
    return `${day} · ${place}`;
}

/** Por qué la celda de Últ. pago viene vacía con el crédito LEÍDO: no hay pago que fechar. */
export const LAST_PAYMENT_EMPTY_NOTE =
    'Este cliente todavía no tiene ningún pago aplicado, así que no hay fecha que mostrar.';

/** Por qué la celda de Segmento no trae chips con la lectura de segmentos fallada en el BFF. */
export const SEGMENTS_UNREAD_LABEL = 'Segmentos no leídos';

export const SEGMENTS_UNREAD_NOTE =
    'El BFF no pudo leer los segmentos de este cliente. Sin esa lectura no se puede afirmar a cuáles pertenece: decir «ninguno» aquí sería inventar.';

/** El cliente no pertenece a ningún segmento. Es un dato leído, no un hueco. */
export const NO_SEGMENTS_NOTE =
    'Los segmentos de este cliente se leyeron y no pertenece a ninguno.';

/** La tienda de alta no viaja: el backoffice entrega la tienda donde MÁS compra, que es otra cosa. */
export const ORIGIN_STORE_NO_SOURCE_NOTE =
    'La tienda donde se dio de alta al cliente no viaja en ningún endpoint de esta pantalla: el backoffice de retail entrega la tienda donde más compra, que no es la misma cosa.';

/** La regla que aplica `matchesSci`: un SCI ausente no se puede afirmar dentro de un rango. */
export const SCI_RANGE_FILTER_NOTE = 'Con un rango puesto, el cliente sin SCI queda fuera.';

/** Qué se rompe con más de un vigente: el saldo de la fila los suma y la ficha desglosa uno solo. */
export function activeCreditsAnomalyNote(activeCreditsCount: number): string {
    const count = String(activeCreditsCount);
    return `Anomalía de datos: este cliente tiene ${count} créditos vigentes a la vez y la regla es uno solo. El saldo de esta fila es la SUMA de los ${count}, mientras que la ficha desglosa uno solo de ellos.`;
}

/* ================================================================================
   FICHA 360 — las etiquetas del crédito. Los motivos de KYC, cuenta PCF, documentos y ARCO viven
   ahora en `customer360Gaps`, uno por dato.
   ================================================================================ */

export const CREDIT_STATUS_LABEL: Record<LoanCreditStatusEnum, string> = {
    [LoanCreditStatusEnum.PENDING_ACTIVATION]: 'Pendiente de activación',
    [LoanCreditStatusEnum.ACTIVE]: 'Activo',
    [LoanCreditStatusEnum.IN_ARREARS]: 'En mora',
    [LoanCreditStatusEnum.SETTLED]: 'Liquidado',
    [LoanCreditStatusEnum.CANCELLED]: 'Cancelado',
    [LoanCreditStatusEnum.DISCARDED]: 'Descartado',
    [LoanCreditStatusEnum.WRITTEN_OFF]: 'Baja contable',
};

export const CREDIT_STATUS_CHIP_CLASS: Record<LoanCreditStatusEnum, string> = {
    [LoanCreditStatusEnum.PENDING_ACTIVATION]: 'border-warning text-warning',
    [LoanCreditStatusEnum.ACTIVE]: 'border-success text-success',
    [LoanCreditStatusEnum.IN_ARREARS]: 'border-destructive text-destructive',
    [LoanCreditStatusEnum.SETTLED]: 'border-info text-info',
    [LoanCreditStatusEnum.CANCELLED]: 'border-muted-foreground text-muted-foreground',
    [LoanCreditStatusEnum.DISCARDED]: 'border-muted-foreground text-muted-foreground',
    [LoanCreditStatusEnum.WRITTEN_OFF]: 'border-destructive text-destructive',
};

/* ================================================================================
   HUECOS LEÍDOS DEL PERFIL — el dato llegó y vino vacío. No es un guion mudo ni una falla.
   ================================================================================ */

export const NO_PHONE: Gap = {
    label: 'Sin teléfono',
    note: 'La venta que dio de alta a este cliente no registró un teléfono. El perfil se leyó completo: esto es lo que trae.',
};

/** La inicial del avatar del hero: primer nombre y primer apellido. */
export function customerInitials(name: string | null): string {
    if (!name?.trim()) return '?';
    const parts = name.trim().split(/\s+/);
    return `${parts[0][0]}${parts[1] ? parts[1][0] : ''}`.toUpperCase();
}
